import {flow} from 'lodash';
import memoize from 'memoize-one';

import {ResourceMap, ResourceTable} from '../../libs/shared/core/src/store/commonAppTypes';

export type Updater<T> = (value: T) => T;
export type ObjectUpdater<T> = <U extends T>(value: U) => U;

// https://stackoverflow.com/a/50375286
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void
  ? I
  : never;

export function composeUpdaters<T>(...updaters: Updater<T>[]): Updater<T>;
export function composeUpdaters<T extends readonly unknown[]>(
  ...updaters: {[K in keyof T]: ObjectUpdater<T[K]>}
): ObjectUpdater<UnionToIntersection<T[any]>>;
export function composeUpdaters<T extends readonly unknown[]>(
  ...updaters: {[K in keyof T]: ObjectUpdater<T[K]>}
): ObjectUpdater<UnionToIntersection<T[any]>> {
  return flow(...updaters);
}

export function createObjectPropertyUpdater<K extends string, V>(
  propertyName: K,
  updatePropertyValue: Updater<V>,
): ObjectUpdater<{[key in K]: V}>;
export function createObjectPropertyUpdater<K extends string, V>(
  propertyName: K,
  updatePropertyValue: ObjectUpdater<V>,
): ObjectUpdater<{[key in K]: V}>;
export function createObjectPropertyUpdater<K extends string, V>(
  propertyName: K,
  updatePropertyValue: ObjectUpdater<V>,
): ObjectUpdater<{[key in K]: V}> {
  return createUpdater((object) => {
    const value = object[propertyName];
    const newValue = updatePropertyValue(value);
    if (value === newValue) {
      return object;
    }

    return {
      ...object,
      [propertyName]: newValue,
    };
  });
}

export function createResourceMapValueUpdater<T>(
  resourceId: number | string,
  updateResource: Updater<T>,
): Updater<ResourceMap<T>> {
  return createUpdater((resources) => {
    const resource = resources[resourceId];
    if (!resource) {
      return resources;
    }

    const newResource = updateResource(resource);
    if (resource === newResource) {
      return resources;
    }

    return {
      ...resources,
      [resourceId]: newResource,
    };
  });
}

export function createResourceTableValueUpdater<TId extends string | number, TResource>(
  resourceId: TId,
  updateResource: Updater<TResource>,
): ObjectUpdater<ResourceTable<TId, TResource>> {
  return createObjectPropertyUpdater('byId', createResourceMapValueUpdater(resourceId, updateResource));
}

export function createUpdater<T>(update: Updater<T>): Updater<T> {
  return memoize(update);
}
